import { useEffect } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import debounce from 'lodash/debounce';
import type { RootState } from './store';

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

export type Func = () => void;

// eslint-disable-next-line react-hooks/exhaustive-deps
export const useMount = (func: Func) => useEffect(func, []);

export enum SCROLL_EVENT_TYPE {
    SCROLL = 'scroll',
    WHEEL = 'wheel',
}

let passiveSupported = false;

try {
    const options = {
        get passive() {
            passiveSupported = true;
            return false;
        },
    };

    window.addEventListener(SCROLL_EVENT_TYPE.SCROLL, () => null, options);
    window.removeEventListener(SCROLL_EVENT_TYPE.SCROLL, () => null);
} catch (err: unknown) {
    passiveSupported = false;
}

export const useScroll = (
    ref: React.RefObject<HTMLElement | HTMLDivElement>,
    // eslint-disable-next-line default-param-last
    eventType: SCROLL_EVENT_TYPE = SCROLL_EVENT_TYPE.SCROLL,
    onScroll?: EventListener,
    onScrollEnd?: EventListener,
    deps: React.DependencyList = [],
    onEndDelay = 100,
) => {
    let interval: ReturnType<typeof setInterval> | undefined;

    const __onScroll: EventListener = (event) => {
        onScroll && onScroll(event);
        clearInterval(interval);

        interval = setInterval(() => {
            clearInterval(interval);
            onScrollEnd && onScrollEnd(event);
        }, onEndDelay);
    };

    useEffect(() => {
        if (!ref.current) {
            return;
        }
        const _ref = ref.current;

        _ref?.removeEventListener(eventType, __onScroll);
        _ref.addEventListener(eventType, __onScroll, passiveSupported ? { passive: true } : false);

        return () => {
            _ref?.removeEventListener(eventType, __onScroll);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};


export const useWindowResize = (callback: () => void, delay = 100): void => {
    useEffect(() => {
        const handleResize = debounce(callback, delay);

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [callback, delay]);
};
